import { createContext, useState, useCallback, useMemo } from 'react';

import zh from './locales/zh';
import en from './locales/en';

export type SupportedLang = 'zh' | 'en';

interface II18nContext {
  lang: SupportedLang;
  t: (key: string, params?: Record<string, string | number>) => string;
  setLang: (lang: SupportedLang) => void;
}

const translations: Record<SupportedLang, Record<string, string>> = { zh, en };

const LANG_COOKIE_KEY = 'lang';

const getLangFromCookie = (): SupportedLang => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${LANG_COOKIE_KEY}=`));
  const value = match?.split('=')[1];

  if (value === 'en' || value === 'zh') return value;

  return 'zh';
};

const setLangCookie = (lang: SupportedLang) => {
  const maxAge = 365 * 24 * 60 * 60;
  document.cookie = `${LANG_COOKIE_KEY}=${lang};path=/;max-age=${maxAge};SameSite=Lax`;
};

export const I18nContext = createContext<II18nContext>({
  lang: 'zh',
  t: (key: string) => key,
  setLang: () => {},
});

interface II18nProviderProps {
  children: React.ReactNode;
}

export const I18nProvider = ({ children }: II18nProviderProps) => {
  const [lang, setLangState] = useState<SupportedLang>(getLangFromCookie);

  const setLang = useCallback((newLang: SupportedLang) => {
    setLangState(newLang);
    setLangCookie(newLang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let text = translations[lang]?.[key] ?? translations.zh?.[key] ?? key;

      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
      }

      return text;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, t, setLang }), [lang, t, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
